import React, { useState, useEffect } from "react";
import { AiOutlineFund, AiOutlineStar, AiOutlineLineChart, AiOutlineBulb } from "react-icons/ai";
import { BsGraphUpArrow, BsGraphUp, BsCashStack, BsGrid3X3 } from "react-icons/bs";
import { IoAnalyticsSharp } from "react-icons/io5";
import Logo from "../../static/light-logo.svg";
import DarkLogo from "../../static/darkLogo.svg";
import "../../styles/theme-tokens.css";
import "../../styles/glass-tokens.css";
import "./authScreenLayout.css";

/* ─────────────────────────────────────────────────────────────── */
/*  SLIDE VISUALS — inline SVG/markup, colored entirely from the    */
/*  shared --vi-accent/--vi-accent-2/--vi-text-* tokens so they     */
/*  follow whichever dark/light mode + blue/teal palette is active. */
/* ─────────────────────────────────────────────────────────────── */

const VisFundamental = () => (
  <svg viewBox="0 0 200 72" fill="none" style={{ width: "100%", height: "72px" }}>
    {[18, 36, 54].map((y) => (
      <line key={y} x1="0" y1={y} x2="200" y2={y} style={{ stroke: "var(--vi-card-border)" }} strokeWidth="1" />
    ))}
    {[
      { x: 12, h: 48 },
      { x: 58, h: 58 },
      { x: 104, h: 38 },
      { x: 150, h: 52 },
    ].map(({ x, h }, i) => (
      <rect key={`a${i}`} x={x} y={72 - h} width={14} height={h} rx="3" style={{ fill: "var(--vi-accent)", opacity: 0.75 }} />
    ))}
    {[
      { x: 30, h: 36 },
      { x: 76, h: 44 },
      { x: 122, h: 56 },
      { x: 168, h: 40 },
    ].map(({ x, h }, i) => (
      <rect key={`b${i}`} x={x} y={72 - h} width={14} height={h} rx="3" style={{ fill: "var(--vi-accent-2)", opacity: 0.55 }} />
    ))}
    {["P/E", "ROE", "EPS", "Div"].map((lbl, i) => (
      <text key={lbl} x={24 + i * 46} y="71" fontSize="7.5" style={{ fill: "var(--vi-text-tertiary)" }} textAnchor="middle">
        {lbl}
      </text>
    ))}
    <rect x="0" y="0" width="7" height="7" rx="1.5" style={{ fill: "var(--vi-accent)", opacity: 0.75 }} />
    <text x="10" y="6.5" fontSize="7" style={{ fill: "var(--vi-text-secondary)" }}>
      Stock A
    </text>
    <rect x="48" y="0" width="7" height="7" rx="1.5" style={{ fill: "var(--vi-accent-2)", opacity: 0.55 }} />
    <text x="58" y="6.5" fontSize="7" style={{ fill: "var(--vi-text-secondary)" }}>
      Stock B
    </text>
  </svg>
);

const VisDynamic = () => {
  const l1 = "M0,64 C25,58 40,50 60,44 S90,36 110,30 S145,18 160,14 S185,10 200,8";
  const l2 = "M0,68 C20,64 45,58 70,54 S100,46 125,40 S160,30 175,26 S190,22 200,20";
  return (
    <svg viewBox="0 0 200 72" fill="none" style={{ width: "100%", height: "72px" }}>
      <defs>
        <linearGradient id="vi-auth-dg1" x1="0" y1="0" x2="0" y2="1">
          <stop offset="0%" style={{ stopColor: "var(--vi-accent)", stopOpacity: 0.3 }} />
          <stop offset="100%" style={{ stopColor: "var(--vi-accent)", stopOpacity: 0 }} />
        </linearGradient>
        <linearGradient id="vi-auth-dg2" x1="0" y1="0" x2="0" y2="1">
          <stop offset="0%" style={{ stopColor: "var(--vi-accent-2)", stopOpacity: 0.2 }} />
          <stop offset="100%" style={{ stopColor: "var(--vi-accent-2)", stopOpacity: 0 }} />
        </linearGradient>
      </defs>
      <path d={`${l2} L200,72 L0,72 Z`} fill="url(#vi-auth-dg2)" />
      <path d={`${l1} L200,72 L0,72 Z`} fill="url(#vi-auth-dg1)" />
      <path d={l2} style={{ stroke: "var(--vi-accent-2)" }} strokeWidth="1.5" strokeLinecap="round" />
      <path d={l1} style={{ stroke: "var(--vi-accent)" }} strokeWidth="1.8" strokeLinecap="round" />
      <circle cx="200" cy="8" r="3" style={{ fill: "var(--vi-accent)" }} />
      <circle cx="200" cy="20" r="3" style={{ fill: "var(--vi-accent-2)" }} />
    </svg>
  );
};

const VisSingleStock = () => {
  const path = "M0,58 C20,54 34,44 50,42 S82,30 100,28 S132,16 150,18 S178,10 200,6";
  return (
    <svg viewBox="0 0 200 72" fill="none" style={{ width: "100%", height: "72px" }}>
      <defs>
        <linearGradient id="vi-auth-ss1" x1="0" y1="0" x2="0" y2="1">
          <stop offset="0%" style={{ stopColor: "var(--vi-accent)", stopOpacity: 0.28 }} />
          <stop offset="100%" style={{ stopColor: "var(--vi-accent)", stopOpacity: 0 }} />
        </linearGradient>
      </defs>
      <path d={`${path} L200,72 L0,72 Z`} fill="url(#vi-auth-ss1)" />
      <path d={path} style={{ stroke: "var(--vi-accent)" }} strokeWidth="2" strokeLinecap="round" />
      <circle cx="200" cy="6" r="3.5" style={{ fill: "var(--vi-accent)" }} />
      <text x="120" y="42" fontSize="9" fontWeight={700} style={{ fill: "var(--vi-text)" }}>
        JKH.N
      </text>
      <text x="120" y="54" fontSize="8" style={{ fill: "var(--vi-accent)" }}>
        Rs 186.00 (+1.2%)
      </text>
    </svg>
  );
};

const VisBeginner = () => (
  <div>
    {["What is a P/E ratio?", "Reading a balance sheet", "Spotting growth stocks"].map((step, i) => (
      <div key={step} className="vi-auth-beg-row">
        <span className="vi-auth-beg-num">{i + 1}</span>
        <span>{step}</span>
      </div>
    ))}
  </div>
);

const VisFinancials = () => (
  <div>
    {[
      { label: "Revenue", val: "Rs 142.5B", chg: "+8.2%", pos: true },
      { label: "Net Income", val: "Rs 18.3B", chg: "+12.1%", pos: true },
      { label: "EPS", val: "Rs 12.40", chg: "-2.4%", pos: false },
      { label: "EBITDA", val: "Rs 31.7B", chg: "+5.8%", pos: true },
    ].map(({ label, val, chg, pos }) => (
      <div key={label} className="vi-auth-finrow">
        <span className="vi-auth-finrow-label">{label}</span>
        <span className="vi-auth-finrow-val">{val}</span>
        <span className={`vi-auth-finrow-chg ${pos ? "pos" : "neg"}`}>{chg}</span>
      </div>
    ))}
  </div>
);

const VisScreener = () => (
  <div>
    {[
      { ticker: "JKH.N", name: "John Keells Holdings", chg: "+2.45%" },
      { ticker: "HNB", name: "Hatton National Bank", chg: "-0.83%" },
      { ticker: "COMB", name: "Commercial Bank", chg: "+1.12%" },
      { ticker: "DIAL", name: "Dialog Axiata", chg: "+0.64%" },
    ].map(({ ticker, name, chg }) => (
      <div key={ticker} className="vi-auth-scrrow">
        <span className="vi-auth-scrrow-ticker">{ticker}</span>
        <span className="vi-auth-scrrow-name">{name}</span>
        <span className={`vi-auth-scrrow-chg ${chg.startsWith("+") ? "pos" : "neg"}`}>{chg}</span>
      </div>
    ))}
  </div>
);

const VisWatchlist = () => (
  <div>
    {[
      { ticker: "JKH", name: "John Keells", price: "Rs 186.00", chg: "+1.2%", pos: true },
      { ticker: "HNB", name: "Hatton NB", price: "Rs 245.50", chg: "+0.4%", pos: true },
      { ticker: "COMB", name: "Commercial", price: "Rs 112.25", chg: "-0.8%", pos: false },
      { ticker: "DIAL", name: "Dialog", price: "Rs 14.80", chg: "+2.1%", pos: true },
    ].map(({ ticker, name, price, chg, pos }) => (
      <div key={ticker} className="vi-auth-wlrow">
        <div className="vi-auth-wlrow-left">
          <span className="vi-auth-wlrow-ticker">{ticker}</span>
          <span className="vi-auth-wlrow-name">{name}</span>
        </div>
        <div className="vi-auth-wlrow-right">
          <span className="vi-auth-wlrow-price">{price}</span>
          <span className={`vi-auth-wlrow-chg ${pos ? "pos" : "neg"}`}>{chg}</span>
        </div>
      </div>
    ))}
  </div>
);

const VisHeatmap = () => {
  const cells = [
    { label: "Bank", val: 1.8 },
    { label: "Manuf", val: 0.4 },
    { label: "Telecom", val: -0.6 },
    { label: "Energy", val: 2.1 },
    { label: "Health", val: 0.8 },
    { label: "R.Estate", val: -1.2 },
    { label: "Tech", val: 3.2 },
    { label: "Retail", val: -0.3 },
    { label: "Tourism", val: 1.1 },
    { label: "Agri", val: -0.7 },
  ];
  const cellStyle = (v: number): React.CSSProperties => {
    if (v > 1.5) return { background: "color-mix(in srgb, var(--vi-accent) 55%, transparent)" };
    if (v > 0) return { background: "color-mix(in srgb, var(--vi-accent) 26%, transparent)" };
    if (v > -1) return { background: "color-mix(in srgb, var(--vi-danger) 26%, transparent)" };
    return { background: "color-mix(in srgb, var(--vi-danger) 50%, transparent)" };
  };
  return (
    <div className="vi-auth-heatmap">
      {cells.map(({ label, val }) => (
        <div key={label} className="vi-auth-heatmap-cell" style={cellStyle(val)}>
          <div className="vi-auth-heatmap-label">{label}</div>
          <div className="vi-auth-heatmap-val">
            {val > 0 ? "+" : ""}
            {val}%
          </div>
        </div>
      ))}
    </div>
  );
};

const VisTechnical = () => {
  const candles = [
    { x: 10, open: 52, close: 44, high: 55, low: 42 },
    { x: 32, open: 44, close: 50, high: 53, low: 43 },
    { x: 54, open: 50, close: 46, high: 52, low: 44 },
    { x: 76, open: 46, close: 56, high: 58, low: 45 },
    { x: 98, open: 56, close: 60, high: 62, low: 54 },
    { x: 120, open: 60, close: 54, high: 62, low: 52 },
    { x: 142, open: 54, close: 62, high: 64, low: 52 },
    { x: 164, open: 62, close: 58, high: 65, low: 56 },
  ];
  const maPoints = candles.map((c) => ({ x: c.x + 8, y: (c.open + c.close) / 2 }));
  const maPath = maPoints.map((p, i) => `${i === 0 ? "M" : "L"}${p.x},${p.y}`).join(" ");
  return (
    <svg viewBox="0 0 190 72" fill="none" style={{ width: "100%", height: "72px" }}>
      {[18, 36, 54].map((y) => (
        <line key={y} x1="0" y1={y} x2="190" y2={y} style={{ stroke: "var(--vi-card-border)" }} strokeWidth="1" />
      ))}
      {candles.map(({ x, open, close, high, low }) => {
        const isBull = close < open;
        const top = Math.min(open, close);
        const height = Math.abs(open - close) || 1;
        return (
          <g key={x}>
            <line
              x1={x + 8}
              y1={high}
              x2={x + 8}
              y2={low}
              style={{ stroke: isBull ? "var(--vi-accent)" : "var(--vi-danger)" }}
              strokeWidth="1"
            />
            <rect
              x={x}
              y={top}
              width={16}
              height={height}
              rx="1.5"
              style={{ fill: isBull ? "var(--vi-accent)" : "var(--vi-danger)", opacity: 0.85 }}
            />
          </g>
        );
      })}
      <path d={maPath} style={{ stroke: "var(--vi-accent-2)" }} strokeWidth="1.5" strokeDasharray="3 2" strokeLinecap="round" fill="none" />
      <text x="0" y="70" fontSize="7" style={{ fill: "var(--vi-text-tertiary)" }}>
        MA(14)
      </text>
    </svg>
  );
};

/* ─────────────────────────────────────────────────────────────── */
/*  SLIDES — one per customer-facing feature (same order/labels as  */
/*  the app's own sidebar in Sidebar.tsx, excluding admin-only      */
/*  tools). Existing 4 keep their original titles/subtitles/icons;  */
/*  the 9 new ones follow the same short, benefit-first phrasing.   */
/* ─────────────────────────────────────────────────────────────── */

const slides = [
  {
    icon: <AiOutlineFund />,
    title: "Compare",
    subtitle: "Compare and visualize trends and performance across multiple stocks.",
    visual: <VisFundamental />,
    stats: [
      { val: "500+", lbl: "Stocks" },
      { val: "20+", lbl: "Metrics" },
      { val: "3", lbl: "Exchanges" },
    ],
  },
  {
    icon: <BsGraphUpArrow />,
    title: "Dynamic Graphs",
    subtitle: "Analyze key financial metrics and ratios of multiple stocks through customizable, interactive graphs",
    visual: <VisDynamic />,
    stats: [
      { val: "15+", lbl: "Chart Types" },
      { val: "Multi", lbl: "Stock" },
      { val: "Live", lbl: "Data" },
    ],
  },
  {
    icon: <BsGraphUp />,
    title: "Detailed Analysis",
    subtitle: "Deep dive into any single stock's fundamentals, technicals, and news in one unified view.",
    visual: <VisSingleStock />,
    stats: [
      { val: "360°", lbl: "Analysis" },
      { val: "Peer", lbl: "Comparison" },
      { val: "Live", lbl: "Pricing" },
    ],
  },
  {
    icon: <AiOutlineBulb />,
    title: "Beginner Analysis",
    subtitle: "Simple, jargon-free breakdowns of key metrics — built for investors just getting started.",
    visual: <VisBeginner />,
    stats: [
      { val: "Easy", lbl: "Explanations" },
      { val: "Key", lbl: "Metrics" },
      { val: "No", lbl: "Jargon" },
    ],
  },
  {
    icon: <BsCashStack />,
    title: "Financials",
    subtitle: "Dive deep into income statements, balance sheets, and cash flows — quarterly or annually.",
    visual: <VisFinancials />,
    stats: [
      { val: "5-Year", lbl: "History" },
      { val: "Q & A", lbl: "Periods" },
      { val: "3", lbl: "Formats" },
    ],
  },
  {
    icon: <IoAnalyticsSharp />,
    title: "Screener",
    subtitle: "Advanced stock screening and analysis tool. Screen Smarter, Invest Better. Find Winning Stocks Instantly!",
    visual: <VisScreener />,
    stats: [
      { val: "50+", lbl: "Filters" },
      { val: "Live", lbl: "Updates" },
      { val: "CSV", lbl: "Export" },
    ],
  },
  {
    icon: <AiOutlineStar />,
    title: "Watchlist",
    subtitle: "Track your favourite stocks and monitor price movements at a glance, in real time.",
    visual: <VisWatchlist />,
    stats: [
      { val: "Custom", lbl: "Lists" },
      { val: "Price", lbl: "Tracking" },
      { val: "Portfolio", lbl: "View" },
    ],
  },
  {
    icon: <BsGrid3X3 />,
    title: "Market Heatmap",
    subtitle: "Instantly visualize market-wide performance by sector, size, and price movement.",
    visual: <VisHeatmap />,
    stats: [
      { val: "Sector", lbl: "View" },
      { val: "Size &", lbl: "Perf" },
      { val: "Live", lbl: "Data" },
    ],
  },
  {
    icon: <AiOutlineLineChart />,
    title: "Technical Analysis",
    subtitle: "Candlestick charts with 12+ indicators — RSI, MACD, Bollinger Bands and more.",
    visual: <VisTechnical />,
    stats: [
      { val: "12+", lbl: "Indicators" },
      { val: "Candle", lbl: "Charts" },
      { val: "Live", lbl: "Feed" },
    ],
  },
];

/* ─────────────────────────────────────────────────────────────── */
/*  LAYOUT COMPONENT                                               */
/* ─────────────────────────────────────────────────────────────── */

const AuthScreenLayout = ({ children }: { children: any }) => {
  const [currentSlide, setCurrentSlide] = useState(0);
  const [animKey, setAnimKey] = useState(0);

  const goToSlide = (index: number) => {
    setCurrentSlide(index);
    setAnimKey((k) => k + 1);
  };

  useEffect(() => {
    const timer = setInterval(() => {
      setCurrentSlide((prev) => (prev + 1) % slides.length);
      setAnimKey((k) => k + 1);
    }, 4500);

    return () => clearInterval(timer);
  }, []);

  return (
    <div className="vi-auth-shell flex md:flex-row flex-col min-h-screen bg-gray-50 w-full gap-x-40">
      <div className="vi-auth-left relative md:w-1/2 overflow-hidden shadow-md">
        {/* Slide progress bar — key forces the fill animation to restart
            on every slide change (auto-advance or manual dot click). */}
        <div className="vi-auth-progress">
          <div key={animKey} className="vi-auth-progress-fill" />
        </div>

        <div className="vi-auth-grid" />
        <div
          className="vi-auth-glow"
          style={{
            width: 340,
            height: 340,
            top: -90,
            right: -90,
            background: "radial-gradient(circle, color-mix(in srgb, var(--vi-accent) 10%, transparent) 0%, transparent 70%)",
          }}
        />
        <div
          className="vi-auth-glow"
          style={{
            width: 280,
            height: 280,
            bottom: 90,
            left: -70,
            background: "radial-gradient(circle, color-mix(in srgb, var(--vi-accent-2) 11%, transparent) 0%, transparent 70%)",
          }}
        />

        {/* Brand */}
        <div className="vi-auth-brand">
          <img src={Logo} alt="ValueIt" className="vi-auth-logo-light" style={{ height: 44, width: "auto" }} />
          <img src={DarkLogo} alt="ValueIt" className="vi-auth-logo-dark" style={{ height: 44, width: "auto" }} />
        </div>
        <div className="vi-auth-brand-divider" />

        {/* Slides */}
        {slides.map((slide, index) => (
          <div
            key={index}
            className="vi-auth-slide"
            style={{
              opacity: currentSlide === index ? 1 : 0,
              pointerEvents: currentSlide === index ? "auto" : "none",
            }}
          >
            <div
              key={currentSlide === index ? `card-${animKey}` : `card-idle-${index}`}
              className="vi-glass-card vi-auth-slide-card vi-auth-slide-card-enter"
            >
              <div className="vi-auth-slide-icon">{slide.icon}</div>
              <h2 className="vi-auth-slide-title">{slide.title}</h2>
              <p className="vi-auth-slide-sub">{slide.subtitle}</p>
              <div className="vi-auth-slide-visual">{slide.visual}</div>
              <div className="vi-auth-slide-stats">
                {slide.stats.map(({ val, lbl }) => (
                  <div key={lbl} className="vi-auth-stat">
                    <span className="vi-auth-stat-val">{val}</span>
                    <span className="vi-auth-stat-lbl">{lbl}</span>
                  </div>
                ))}
              </div>
            </div>
          </div>
        ))}

        {/* Dots */}
        <div className="vi-auth-dots">
          {slides.map((_, index) => (
            <button
              key={index}
              onClick={() => goToSlide(index)}
              className={`vi-auth-dot${currentSlide === index ? " vi-auth-dot-active" : ""}`}
              aria-label={`Go to slide ${index + 1}`}
            />
          ))}
        </div>

        {/* Trust badge */}
        <div className="vi-auth-trust">
          <span className="vi-auth-trust-dot" />
          Data-driven valuations · AI-powered insights · Smart decisions
        </div>
      </div>

      {/* Right side - Login/Register form */}
      <div className="md:w-1/2 flex items-center">{children}</div>
    </div>
  );
};

export default AuthScreenLayout;
